import { Component, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouterLink } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { ModelVersionService } from '../../../services/model-version.service';
import { ModelVersion } from '../../../models/model-version.model';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-model-version-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    RouterLink,
    ToggleSwitchModule,
    ToastModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './model-version-list.component.html',
})
export class ModelVersionListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private modelVersionService = inject(ModelVersionService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  paginationService = inject(PaginationService);

  modelVersions = signal<ModelVersion[]>([]);

  submitted: boolean = false;

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  nameFilter?: string;
  descriptionFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any) {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);
    this.modelVersionService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.nameFilter,
        this.descriptionFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.modelVersions.set(page.items as ModelVersion[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar as versões de modelo',
              life: 3000,
            });
          }
        },
      });
  }

  requestUpdateStatus(item: ModelVersion, event: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Você tem certeza que quer alterar o status da versão?',
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Alterar',
      },
      accept: () => {
        this.updateStatus(item);
      },
    });
  }

  private updateStatus(item: ModelVersion): void {
    this.loading.set(true);
    this.modelVersionService.updateStatus(item.id, !item.isActive).subscribe({
      next: () => {
        this.loading.set(false);

        item.isActive = !item.isActive;

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Status atualizado com sucesso',
          life: 2000,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao atualizar o status',
            life: 3000,
          });
        }
      },
    });
  }
}
